import React, { useState, useEffect } from 'react';
import { Cpu, Volume2, VolumeX, Play, Pause, Loader } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import openaiTTS from '../utils/openaiTTS';

const AIMessageBubble = ({ content, theme, index, formatAnswer, voiceState, onTTSStateChange, onAutoRestart, inputMode, onInputModeChange, onVoiceStateChange, onMarkAsRead, messageSource }) => {
  const { currentThemeName } = useTheme();
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [isTyping, setIsTyping] = useState(true);
  const [displayedContent, setDisplayedContent] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Read button state
  const [isReading, setIsReading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [hasRead, setHasRead] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [autoReadDone, setAutoReadDone] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(() => {
    // Load saved voice preference from localStorage, default to echo
    const savedVoice = localStorage.getItem('oaklandAI_selectedVoice');
    // If no saved voice or if it's the old default 'alloy', use 'echo'
    if (!savedVoice || savedVoice === 'alloy') {
      localStorage.setItem('oaklandAI_selectedVoice', 'echo');
      return 'echo';
    }
    return savedVoice;
  });

  useEffect(() => {
    // Trigger animation immediately for better mobile performance
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (isTyping) {
      // Faster typing in voice mode for better responsiveness
      const typingSpeed = inputMode === 'voice' ? 5 : 10; // 2x faster in voice mode
      
      const typeTimer = setTimeout(() => {
        if (currentIndex < content.length) {
          setDisplayedContent(content.slice(0, currentIndex + 1));
          setCurrentIndex(currentIndex + 1);
        } else {
          setIsTyping(false);
        }
      }, typingSpeed);

      return () => clearTimeout(typeTimer);
    }
  }, [isTyping, currentIndex, content, inputMode]);

  // Fallback: ensure content is always displayed after a reasonable time
  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      if (isTyping) {
        setDisplayedContent(content);
        setIsTyping(false);
      }
    }, 3000); // 3 second fallback

    return () => clearTimeout(fallbackTimer);
  }, [content, isTyping]);

  // Reset autoReadDone when content changes (new message)
  useEffect(() => {
    setAutoReadDone(false);
  }, [content]);

  // Listen for mark as read signal from parent
  useEffect(() => {
    if (onMarkAsRead) {
      console.log('TTS Debug: Setting up mark as read callback');
      onMarkAsRead(() => {
        console.log('TTS Debug: Received mark as read signal, marking current message as read');
        setAutoReadDone(true);
      });
    }
  }, [onMarkAsRead]);

  // Auto-restart function after TTS ends
  const handleAutoRestart = () => {
    console.log('TTS Debug: Auto-restarting voice mode');
    if (onAutoRestart) {
      onAutoRestart();
    }
  };

  // Auto-activate read button when voice mode is active (only for NEW messages from voice mode)
  useEffect(() => {
    console.log('TTS Debug: Auto-read effect triggered', {
      voiceState: voiceState ? { isRecording: voiceState.isRecording, isAutoRecording: voiceState.isAutoRecording } : null,
      isTyping,
      isReading,
      isLoading,
      autoReadDone,
      inputMode,
      messageSource
    });
    
    // NEVER auto-read if message is from keyboard to text
    if (messageSource === 'fromKeyboardToText') {
      console.log('TTS Debug: Message is from keyboard to text, skipping auto-read completely');
      return;
    }
    
    // VOICE MODE: Start TTS immediately (faster response)
    if (voiceState && 
        (voiceState.isRecording || voiceState.isAutoRecording) && 
        !isReading && 
        !isLoading && 
        !autoReadDone && 
        inputMode === 'voice' &&
        (voiceState.isRecording || voiceState.isAutoRecording) && // Double check voice is actually active
        messageSource === 'fromVoiceToText') { // Only for voice to text messages
      
      console.log('TTS Debug: Voice mode active with voice-to-text message, starting TTS immediately');
      // Start TTS immediately in voice mode for fastest response
      // Use a very short delay to allow some content to be displayed first
      setTimeout(() => {
        handleReadClick();
      }, 0); // No delay for immediate response in voice mode
    } else if (inputMode === 'keyboard') {
      console.log('TTS Debug: Keyboard mode active, skipping auto-read');
    } else {
      console.log('TTS Debug: Auto-read conditions not met', {
        voiceActive: voiceState && (voiceState.isRecording || voiceState.isAutoRecording),
        notTyping: !isTyping,
        notReading: !isReading,
        notLoading: !isLoading,
        notAutoReadDone: !autoReadDone,
        isVoiceMode: inputMode === 'voice',
        messageSource
      });
    }
  }, [voiceState, isTyping, isReading, isLoading, autoReadDone, inputMode, messageSource]);

  // Notify parent when TTS starts/stops to control microphone
  useEffect(() => {
    if (onTTSStateChange) {
      onTTSStateChange({
        isReading,
        isPaused,
        isLoading
      });
    }
  }, [isReading, isPaused, isLoading, onTTSStateChange]);

  // Cleanup TTS on unmount
  useEffect(() => {
    return () => {
      openaiTTS.stop();
    };
  }, []);

  // Handle read button click
  const handleReadClick = async () => {
    if (!content) return;
    
    // NEVER read if message is from keyboard to text
    if (messageSource === 'fromKeyboardToText') {
      console.log('TTS Debug: Message is from keyboard to text, TTS disabled');
      return;
    }
    
    // Don't read in keyboard mode
    if (inputMode === 'keyboard') {
      console.log('TTS Debug: Keyboard mode active, TTS disabled');
      return;
    }
    
    // Don't read if already read automatically
    if (autoReadDone) {
      console.log('TTS Debug: Message already read automatically, skipping');
      return;
    }
    
    // In voice mode, use the current displayed content for TTS (even if still typing)
    const contentToRead = inputMode === 'voice' ? displayedContent : content;
    if (!contentToRead) {
      console.log('TTS Debug: No content to read');
      return;
    }

    if (isReading) {
      // Pause/Resume functionality
      if (isPaused) {
        openaiTTS.resume();
        setIsPaused(false);
      } else {
        openaiTTS.pause();
        setIsPaused(true);
      }
    } else {
      // Start reading with OpenAI TTS
      // In voice mode, use displayed content for faster response; otherwise use full content
      const textToRead = inputMode === 'voice' ? displayedContent : content;
      
      console.log('TTS Debug: Starting to read text:', textToRead.substring(0, 100) + '...');
      
      // Stop any existing TTS
      openaiTTS.stop();
      
      setIsLoading(true);
      
      try {
        await openaiTTS.speak(textToRead, {
          voice: selectedVoice,
          onStart: () => {
            console.log('TTS Debug: Audio started playing');
            setIsReading(true);
            setIsPaused(false);
            setHasRead(true);
            setIsLoading(false);
          },
          onEnd: () => {
            console.log('TTS Debug: Audio finished playing');
            setIsReading(false);
            setIsPaused(false);
            setIsLoading(false);
            setAutoReadDone(true);
            
            // Auto-restart voice mode after TTS ends naturally
            // (Manual stop will be handled by handleStopReading and won't trigger this)
            setTimeout(() => {
              handleAutoRestart();
            }, 500);
          },
          onError: (error) => {
            console.error('OpenAI TTS error:', error);
            setIsReading(false);
            setIsPaused(false);
            setIsLoading(false);
            
            // Show more specific error message based on error type
            let errorMessage = 'Text-to-speech is not available. Please check your connection and try again.';
            
            if (error.name === 'NotAllowedError' || error.message?.includes('not allowed') || error.message?.includes('user interaction')) {
              errorMessage = 'Audio playback requires user interaction. Please click the play button or interact with the page first, then try again.';
            } else if (error.message) {
              errorMessage = error.message;
            }
            
            alert(errorMessage);
          }
        });
      } catch (error) {
        console.error('OpenAI TTS error:', error);
        setIsReading(false);
        setIsPaused(false);
        setIsLoading(false);
        
        // Show more specific error message based on error type
        let errorMessage = 'Text-to-speech is not available. Please check your connection and try again.';
        
        if (error.name === 'NotAllowedError' || error.message?.includes('not allowed') || error.message?.includes('user interaction')) {
          errorMessage = 'Audio playback requires user interaction. Please click the play button or interact with the page first, then try again.';
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        alert(errorMessage);
      }
    }
  };

  // Handle stop reading
  const handleStopReading = () => {
    console.log('TTS Debug: Manual stop requested');
    openaiTTS.stop(); // This will NOT call onEnd callback (manual stop)
    
    // Manually update local state since onEnd won't be called
    setIsReading(false);
    setIsPaused(false);
    setIsLoading(false);
    setAutoReadDone(true); // Mark as read to prevent re-reading
    
    // Notify parent component about TTS state changes
    if (onTTSStateChange) {
      console.log('TTS Debug: Notifying parent about TTS stop');
      onTTSStateChange({
        isReading: false,
        isPaused: false,
        isLoading: false
      });
    }
    
    // Switch to keyboard mode when TTS is manually stopped
    if (onInputModeChange) {
      console.log('TTS Debug: TTS stopped manually, switching to keyboard mode');
      onInputModeChange('keyboard');
    }
    
    // Disable auto-recording when switching to keyboard mode
    if (onVoiceStateChange) {
      console.log('TTS Debug: TTS stopped manually, disabling auto-recording');
      onVoiceStateChange({
        isRecording: false,
        isAutoRecording: false,
        hasStartedManually: false,
        isAutoDisabled: true // Disable auto-recording permanently
      });
    }
  };

  return (
    <div 
      className="ai-message-container"
      style={{
        display: 'flex',
        justifyContent: 'flex-start',
        marginBottom: '16px',
        padding: '0 8px',
        opacity: 1, // Always visible for mobile stability
        transform: 'translateY(0)',
        transition: 'opacity 0.3s ease-out'
      }}
    >
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        maxWidth: '70%',
        position: 'relative'
      }}>
        {/* AI Avatar */}
        <div className="ai-message-avatar" style={{
          padding: '8px 16px',
          borderRadius: '20px',
          background: 'rgba(59, 130, 246, 0.1)',
          border: `1px solid rgba(59, 130, 246, 0.3)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          animation: 'aiAvatarSlideIn 0.3s ease-out',
          gap: '8px',
          backdropFilter: 'blur(10px)',
          opacity: 0.9
        }}>
          <Cpu size={16} style={{ color: theme.primary || '#3b82f6' }} />
          <div style={{
            fontSize: '13px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #22c55e 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: '600',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            letterSpacing: '0.3px'
          }}>
            Oakland AI
          </div>
        </div>

        {/* Message Bubble */}
        <div 
          className={`ai-message-bubble ${autoReadDone ? 'autoReadDone' : ''}`}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: '#ffffff',
            padding: '16px 20px',
            borderRadius: '20px',
            borderBottomLeftRadius: '8px',
            boxShadow: '0 8px 32px rgba(59, 130, 246, 0.2)',
            position: 'relative',
            overflow: 'hidden',
            animation: 'aiBubbleSlideIn 0.3s ease-out',
            marginBottom: '8px'
          }}
        >
          {/* Typing Cursor */}
          {isTyping && (
            <span 
              className="typing-cursor"
              style={{
                display: 'inline-block',
                width: '2px',
                height: '1.2em',
                background: '#ffffff',
                marginLeft: '2px',
                animation: 'typingBlink 1s infinite'
              }}
            />
          )}

          {/* Message Content */}
          <div style={{
            lineHeight: '1.5',
            fontSize: '15px',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            color: '#ffffff',
            fontWeight: '400'
          }}>
            {formatAnswer ? formatAnswer(displayedContent, index) : displayedContent}
          </div>

          {/* AI Processing Glow Effect */}
          <div 
            className="ai-glow-effect"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(45deg, transparent, rgba(255,255,255,0.1), transparent)`,
              animation: isTyping ? 'aiGlowSweep 2s infinite' : 'none',
              pointerEvents: 'none'
            }}
          />
        </div>

        {/* Read Button - Only show when typing is complete and not currently reading/loading and voice mode and from voice to text */}
        {!isTyping && !isReading && !isLoading && inputMode === 'voice' && messageSource === 'fromVoiceToText' && (
          <div 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '8px',
              padding: '10px 16px',
              background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
              borderRadius: '12px',
              border: '2px solid #ffc107',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              opacity: hasRead ? 0.7 : 1,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              fontWeight: '600'
            }}
            onClick={(e) => {
              console.log('TTS Debug: Read button clicked');
              console.log('TTS Debug: Content length:', content?.length);
              console.log('TTS Debug: Content preview:', content?.substring(0, 50));
              e.preventDefault();
              e.stopPropagation();
              handleReadClick();
            }}
            onMouseEnter={e => {
              if (!hasRead) {
                e.target.style.background = 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
              }
            }}
            onMouseLeave={e => {
              if (!hasRead) {
                e.target.style.background = 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }
            }}
            title={hasRead ? t('fullchat.alreadyRead') : t('fullchat.clickToReadAloud')}
          >
            <Volume2 size={16} color="#1f2937" />
            <span style={{
              fontSize: '13px',
              fontWeight: '700',
              color: '#1f2937',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}>
              {hasRead ? t('fullchat.readAgain') : t('fullchat.readAloud')}
            </span>
          </div>
        )}

        {/* Loading State - compact pill when generating TTS */}
        {isLoading && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            marginTop: '8px',
            padding: '6px 10px',
            background: 'rgba(251, 191, 36, 0.2)',
            border: '1px solid rgba(251, 191, 36, 0.5)',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: '600',
            color: 'var(--primary-text, #1e293b)',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
          }}>
            <Loader size={14} color="currentColor" style={{ flexShrink: 0, animation: 'spin 1s linear infinite' }} />
            <span>{t('fullchat.generatingAudio')}</span>
          </div>
        )}

        {/* AI Answering Status - single compact pill when reading */}
        {isReading && !isLoading && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            marginTop: '8px',
            padding: '6px 10px',
            background: 'rgba(251, 191, 36, 0.2)',
            border: '1px solid rgba(251, 191, 36, 0.5)',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: '600',
            color: 'var(--primary-text, #1e293b)',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
          }}>
            <span style={{
              width: '6px',
              height: '6px',
              background: 'currentColor',
              borderRadius: '50%',
              animation: 'pulse 1.5s infinite',
              flexShrink: 0
            }} />
            <span>{t('chat.aiAnswering')}</span>
            <button
              type="button"
              onClick={handleStopReading}
              style={{
                marginLeft: '4px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.4)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--error, #dc2626)',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
              }}
              title="Stop"
            >
              <VolumeX size={12} />
              {t('fullchat.stopReading') || 'Stop'}
            </button>
          </div>
        )}

      </div>

      <style jsx="true">{`
        @keyframes aiAvatarSlideIn {
          from {
            opacity: 0;
            transform: scale(0.8) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes aiBubbleSlideIn {
          from {
            opacity: 0;
            transform: translateX(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }

        @keyframes typingBlink {
          0%, 50% {
            opacity: 1;
          }
          51%, 100% {
            opacity: 0;
          }
        }

        @keyframes aiGlowSweep {
          0% {
            transform: translateX(-100%);
          }
          50% {
            transform: translateX(100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.05);
          }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }


        /* Mobile-specific fixes */
        @media (max-width: 768px) {
          .ai-message-container {
            opacity: 1 !important;
            transform: none !important;
          }
          
          .ai-message-bubble {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
          }
          
          .ai-message-avatar {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
          }
          
          .autoReadDone {
            border: 2px solid #10b981 !important;
            box-shadow: 0 0 20px rgba(16, 185, 129, 0.3) !important;
          }
          
          .autoReadDone::after {
            content: '✓ Read';
            position: absolute;
            top: -8px;
            right: -8px;
            background: #10b981;
            color: white;
            font-size: 10px;
            padding: 2px 6px;
            border-radius: 10px;
            font-weight: 600;
          }
          
          @keyframes thinkingPulse {
            0%, 100% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.1);
              opacity: 0.8;
            }
          }
          
          .thinking-text {
            animation: thinkingPulse 1.5s ease-in-out infinite;
            display: inline-block;
          }
        }
      `}</style>
    </div>
  );
};

export default AIMessageBubble; 